// apps/client/src/pages/MonthlyTableDashboardPage.tsx
// ==========================================================================
// COMMISSION BREAKDOWN (/commission-breakdown), company-wide month by month
// ==========================================================================
// One callable, getMonthlyTableData, fetched through useDashboardQuery.
//
// Two month keys share one row, deliberately:
// - Commission columns (upfront, trail, clawbacks, Line of Trail) are keyed by
//   the commission run month: when the commission was paid.
// - Loans Settled and Settled Amount are keyed by SETTLEMENT DATE, each loan
//   split counted once, from the same shared settlement query as the Dashboard
//   "Loans Settled" card, P&L table and Office Dashboard. The footer shows the
//   backend's distinct totals for the window, not a sum of the rows.
//
// Pure period, month-spine and request rules: utils/commissionBreakdown.ts.
// ==========================================================================

import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';
import { COMMISSION_GST_BASIS } from '../utils/commissionDisplay';
import { useCompanyDataYearRange } from '../hooks/useCompanyDataYearRange';
import { useHideCommissions, useHideLoanVolume } from '../hooks/useHideCommissions';
import { useDashboardQuery } from '../hooks/useDashboardQuery';
import { Table, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { SkeletonTable } from '../components/common/SkeletonLoader';
import { HelpTooltip } from '../components/help/HelpTooltip';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import {
  buildMonthlyTableRequest,
  buildYearOptions,
  defaultYearFor,
  financialYearLabel,
  formatCount,
  monthLabel,
  zeroFillMonths,
  type PeriodMode,
} from '../utils/commissionBreakdown';

type SortField = 'month' | 'upfront' | 'trail' | 'total' | 'settlementCount' | 'settlementAmount' | 'lineOfTrail' | 'clawbacks';
type SortDirection = 'asc' | 'desc';
// Gross/Payouts/Net view (commission_deductions_view, gross_only tenants only).
type ViewMode = 'gross' | 'payouts' | 'net';

const REDACTED = '\u2014';
const SETTLEMENT_HELP = 'By settlement date. Each loan split counted once.';
const LOT_TOTAL_HELP =
  'Line of Trail counts distinct loans paying trail in each month. The same loan pays trail month after month, so the months cannot be added into a total.';

/** Per-payee payout line (broker or referrer), signed-negative cents from backend. */
interface PayoutSplit {
  brokerId?: string;
  name: string;
  upfrontCents: number;
  trailCents: number;
}

interface MonthlyTableRow {
  yearMonth: string;
  monthLabel: string;
  year: number;
  month: number;
  upfrontCommission: number;
  trailCommission: number;
  settlementCount: number;
  settlementAmount: number;
  lineOfTrail: number;
  clawbacks: number;
  // Gross/Payouts/Net: broker+referrer payout totals (negative) + per-payee detail.
  upfrontDeductions: number;
  trailDeductions: number;
  brokerSplits: PayoutSplit[];
  referrerSplits: PayoutSplit[];
}

interface MonthlyTableResponse {
  months?: Array<Record<string, any>>;
  totals?: { uniqueSettlementCount?: number; uniqueSettledCents?: number };
  metadata?: { queryTimeMs?: number; deductionTreatment?: string | null };
}

/** A displayed column: header, row cells and footer read one definition, so they cannot disagree. */
interface Column {
  key: string;
  label: string;
  sortField: SortField;
  align: 'left' | 'right';
  className: string;
  help?: string;
  cell: (row: MonthlyTableRow) => string;
  footer: string;
  footerHelp?: string;
}

function emptyRow(yearMonth: string): MonthlyTableRow {
  const [y, m] = yearMonth.split('-').map((p) => parseInt(p, 10));
  return {
    yearMonth, monthLabel: monthLabel(m), year: y, month: m,
    upfrontCommission: 0, trailCommission: 0, settlementCount: 0, settlementAmount: 0,
    lineOfTrail: 0, clawbacks: 0, upfrontDeductions: 0, trailDeductions: 0,
    brokerSplits: [], referrerSplits: [],
  };
}

const MonthlyTableDashboardPage: React.FC = () => {
  const { userData, companyData, loanBooks = [] } = useOutletContext<any>();
  const hideCommissions = useHideCommissions();
  const hideLoanVolume = useHideLoanVolume();
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  // Year range from BigQuery for the year dropdown.
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  const [periodMode, setPeriodMode] = useState<PeriodMode>('calendar');
  const [selectedYear, setSelectedYear] = useState(() => defaultYearFor('calendar'));
  const [sortField, setSortField] = useState<SortField>('month');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Switching mode lands on the CURRENT period of the new mode (Brisbane clock):
  // carrying a calendar year across would open on the wrong financial year
  // for half of every year.
  const changePeriodMode = (mode: PeriodMode) => {
    if (mode === periodMode) return;
    setPeriodMode(mode);
    if (mode !== 'allTime') setSelectedYear(defaultYearFor(mode));
  };

  // Gross/Payouts/Net view: gated to gross_only tenants with the flag on.
  const flagOn = companyData?.featureFlags?.commission_deductions_view === true;
  const [viewMode, setViewMode] = useState<ViewMode>('gross');
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const toggleMonth = (ym: string) =>
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(ym) ? next.delete(ym) : next.add(ym);
      return next;
    });

  const yearOptions = useMemo(
    () => (periodMode === 'allTime' ? [] : buildYearOptions(periodMode, earliestYear, latestYear)),
    [periodMode, earliestYear, latestYear]
  );

  const requestParams = useMemo(
    () => buildMonthlyTableRequest({
      companyId: userData?.companyId ?? '',
      periodMode,
      selectedYear,
      entityCode: resolvedParams.entityCode,
      templateId: resolvedParams.templateId,
      // Per-payee detail only when the flag is on (founder sets it on
      // gross_only tenants only). Every other request is unchanged.
      includeDeductionDetail: flagOn,
    }),
    [userData?.companyId, periodMode, selectedYear, resolvedParams.entityCode, resolvedParams.templateId, flagOn]
  );

  // Keyed by the full request, so a slow response for an old filter can never
  // overwrite a newer one, and a new filter never shows the previous filter's rows.
  const { data, loading, error } = useDashboardQuery<MonthlyTableResponse>(
    'getMonthlyTableData',
    requestParams
  );

  const deductionTreatment = data?.metadata?.deductionTreatment ?? null;
  const showDeductions = flagOn && deductionTreatment === 'gross_only';
  // If the gate ever turns off, never leave the user stranded on a hidden view.
  const effectiveView: ViewMode = showDeductions ? viewMode : 'gross';
  const queryTimeMs = data?.metadata?.queryTimeMs ?? null;

  const tableData = useMemo<MonthlyTableRow[]>(() => {
    if (!data) return [];
    const rows: MonthlyTableRow[] = (data.months || []).map((row: any) => {
      const [yearStr, monthStr] = String(row.month).split('-');
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      return {
        yearMonth: `${yearStr}-${monthStr}`,
        monthLabel: monthLabel(month),
        year,
        month,
        upfrontCommission: row.upfrontCents || 0,
        trailCommission: row.trailCents || 0,
        settlementCount: row.settlementCount || 0,
        settlementAmount: row.settlementAmountCents || 0,
        lineOfTrail: row.lineOfTrail || 0,
        clawbacks: row.clawbackCents || 0,
        upfrontDeductions: row.upfrontDeductionsCents || 0,
        trailDeductions: row.trailDeductionsCents || 0,
        brokerSplits: row.brokerSplits || [],
        referrerSplits: row.referrerSplits || [],
      };
    });
    // Year windows fill genuine mid-window gaps only; an empty window stays
    // empty (the page's empty state) and nothing fills past this month.
    if (periodMode !== 'allTime') {
      for (const ym of zeroFillMonths(rows.map((r) => r.yearMonth), selectedYear, periodMode)) {
        rows.push(emptyRow(ym));
      }
    }
    return rows;
    // The window is part of the request key, so `data` always belongs to it.
  }, [data, periodMode, selectedYear]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // View-aware displayed values. Gross = commission; Payouts = magnitude of the
  // broker+referrer payout (deduction rows are negative); Net = commission + payout.
  const upfrontFor = (r: MonthlyTableRow) =>
    effectiveView === 'gross' ? r.upfrontCommission
    : effectiveView === 'payouts' ? Math.abs(r.upfrontDeductions)
    : r.upfrontCommission + r.upfrontDeductions;
  const trailFor = (r: MonthlyTableRow) =>
    effectiveView === 'gross' ? r.trailCommission
    : effectiveView === 'payouts' ? Math.abs(r.trailDeductions)
    : r.trailCommission + r.trailDeductions;
  const totalFor = (r: MonthlyTableRow) => upfrontFor(r) + trailFor(r);

  // Sort data
  const sortedData = useMemo(() => {
    const valueOf = (r: MonthlyTableRow): number | string => {
      switch (sortField) {
        case 'month': return r.yearMonth;
        case 'upfront': return upfrontFor(r);
        case 'trail': return trailFor(r);
        case 'total': return totalFor(r);
        case 'settlementCount': return r.settlementCount;
        case 'settlementAmount': return r.settlementAmount;
        case 'lineOfTrail': return r.lineOfTrail;
        // Clawbacks are shown as a magnitude, so they sort as one.
        case 'clawbacks': return Math.abs(r.clawbacks);
        default: return 0;
      }
    };
    return [...tableData].sort((a, b) => {
      const aValue = valueOf(a);
      const bValue = valueOf(b);
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, sortField, sortDirection, effectiveView]);

  // Footer totals. Settlement fields are the backend's distinct totals for the
  // window (a loan split counted once); summing rows is only a fallback.
  const totals = useMemo(() => {
    const summed = sortedData.reduce((acc, row) => ({
      upfront: acc.upfront + row.upfrontCommission,
      trail: acc.trail + row.trailCommission,
      total: acc.total + row.upfrontCommission + row.trailCommission,
      settlementCount: acc.settlementCount + row.settlementCount,
      settlementAmount: acc.settlementAmount + row.settlementAmount,
      clawbacks: acc.clawbacks + row.clawbacks,
      upfrontDeductions: acc.upfrontDeductions + row.upfrontDeductions,
      trailDeductions: acc.trailDeductions + row.trailDeductions,
    }), { upfront: 0, trail: 0, total: 0, settlementCount: 0, settlementAmount: 0, clawbacks: 0, upfrontDeductions: 0, trailDeductions: 0 });
    const backend = data?.totals ?? {};
    return {
      ...summed,
      settlementCount: typeof backend.uniqueSettlementCount === 'number' ? backend.uniqueSettlementCount : summed.settlementCount,
      settlementAmount: typeof backend.uniqueSettledCents === 'number' ? backend.uniqueSettledCents : summed.settlementAmount,
    };
  }, [sortedData, data]);

  // View-aware footer totals (positive magnitudes for payouts).
  const viewTotals = useMemo(() => {
    if (effectiveView === 'payouts') {
      const u = Math.abs(totals.upfrontDeductions);
      const t = Math.abs(totals.trailDeductions);
      return { upfront: u, trail: t, total: u + t };
    }
    if (effectiveView === 'net') {
      const u = totals.upfront + totals.upfrontDeductions;
      const t = totals.trail + totals.trailDeductions;
      return { upfront: u, trail: t, total: u + t };
    }
    return { upfront: totals.upfront, trail: totals.trail, total: totals.total };
  }, [effectiveView, totals]);

  const money = (cents: number) => (hideCommissions ? REDACTED : formatCurrency(cents));
  // Settled Amount is loan volume, not commission: same gate as the Dashboard card.
  const volume = (cents: number) => (hideLoanVolume ? REDACTED : formatCurrency(cents));
  const monthCount = sortedData.length;

  const columns: Column[] = useMemo(() => {
    const month: Column = {
      key: 'month', label: 'Month', sortField: 'month', align: 'left',
      className: 'font-medium text-white',
      cell: (r) => `${r.monthLabel} ${r.year}`,
      footer: effectiveView === 'payouts'
        ? `Total paid out (${monthCount} months)`
        : effectiveView === 'net'
          ? `Total retained (${monthCount} months)`
          : `Total (${monthCount} months)`,
    };
    if (effectiveView !== 'gross') {
      const isPayouts = effectiveView === 'payouts';
      return [
        month,
        { key: 'upfront', label: isPayouts ? 'Upfront Payouts' : 'Net Upfront', sortField: 'upfront', align: 'right', className: 'text-green-400', cell: (r) => money(upfrontFor(r)), footer: money(viewTotals.upfront) },
        { key: 'trail', label: isPayouts ? 'Trail Payouts' : 'Net Trail', sortField: 'trail', align: 'right', className: 'text-blue-400', cell: (r) => money(trailFor(r)), footer: money(viewTotals.trail) },
        { key: 'total', label: isPayouts ? 'Total Paid Out' : 'Net Total', sortField: 'total', align: 'right', className: 'text-yellow-400 font-medium', cell: (r) => money(totalFor(r)), footer: money(viewTotals.total) },
      ];
    }
    return [
      month,
      { key: 'upfront', label: 'Upfront Commission', sortField: 'upfront', align: 'right', className: 'text-green-400', cell: (r) => money(r.upfrontCommission), footer: money(totals.upfront) },
      { key: 'trail', label: 'Trail Commission', sortField: 'trail', align: 'right', className: 'text-blue-400', cell: (r) => money(r.trailCommission), footer: money(totals.trail) },
      { key: 'total', label: 'Total Commission', sortField: 'total', align: 'right', className: 'text-yellow-400 font-medium', cell: (r) => money(r.upfrontCommission + r.trailCommission), footer: money(totals.total) },
      { key: 'settlementCount', label: 'Loans Settled', sortField: 'settlementCount', align: 'right', className: 'text-gray-300', help: SETTLEMENT_HELP, cell: (r) => formatCount(r.settlementCount), footer: formatCount(totals.settlementCount) },
      { key: 'settlementAmount', label: 'Settled Amount', sortField: 'settlementAmount', align: 'right', className: 'text-emerald-400', help: SETTLEMENT_HELP, cell: (r) => volume(r.settlementAmount), footer: volume(totals.settlementAmount) },
      { key: 'lineOfTrail', label: 'Line of Trail', sortField: 'lineOfTrail', align: 'right', className: 'text-purple-400', cell: (r) => formatCount(r.lineOfTrail), footer: REDACTED, footerHelp: LOT_TOTAL_HELP },
      // Shown as a magnitude in red: the column name already says it is money lost.
      { key: 'clawbacks', label: 'Clawbacks', sortField: 'clawbacks', align: 'right', className: 'text-red-400', cell: (r) => money(Math.abs(r.clawbacks)), footer: money(Math.abs(totals.clawbacks)) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveView, totals, viewTotals, monthCount, hideCommissions, hideLoanVolume]);

  const periodCaption = periodMode === 'allTime'
    ? 'All time'
    : periodMode === 'financial'
      ? financialYearLabel(selectedYear)
      : String(selectedYear);

  const ariaSort = (field: SortField): 'ascending' | 'descending' | 'none' =>
    sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-600" aria-hidden="true" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-400" aria-hidden="true" />
      : <ArrowDown className="w-3 h-3 text-blue-400" aria-hidden="true" />;
  };

  const expandable = (r: MonthlyTableRow) =>
    effectiveView !== 'gross' && (r.brokerSplits.length + r.referrerSplits.length) > 0;

  // Per-payee rows under an expanded month (Payouts / Net only). Mirrors the
  // RCTI's broker/referrer deduction sections.
  const renderPayeeRows = (row: MonthlyTableRow) => {
    const isPayouts = effectiveView === 'payouts';
    // Per-payee: payouts → magnitude; net → negative (amount paid out).
    const payeeVal = (cents: number) => (isPayouts ? Math.abs(cents) : -Math.abs(cents));
    const payeeRows = (splits: PayoutSplit[], prefix: string) =>
      splits.map((s, i) => (
        <tr key={`${prefix}-${row.yearMonth}-${i}`} className="bg-gray-900/20 border-b border-gray-800">
          <td className="px-4 py-2 pl-9 text-gray-300">{s.name}</td>
          <td className="px-4 py-2 text-right text-green-400/90">{money(payeeVal(s.upfrontCents))}</td>
          <td className="px-4 py-2 text-right text-blue-400/90">{money(payeeVal(s.trailCents))}</td>
          <td className="px-4 py-2 text-right text-yellow-400/90">{money(payeeVal(s.upfrontCents + s.trailCents))}</td>
        </tr>
      ));
    const subHead = (label: string) => (
      <tr className="bg-gray-900/40">
        <td className="px-4 py-2 pl-9 text-xs uppercase tracking-wide text-blue-300 font-semibold">{label}</td>
        <td /><td /><td />
      </tr>
    );
    return (
      <>
        {row.brokerSplits.length > 0 && subHead('Brokers')}
        {payeeRows(row.brokerSplits, 'b')}
        {row.referrerSplits.length > 0 && subHead('Referrers')}
        {payeeRows(row.referrerSplits, 'r')}
      </>
    );
  };

  const toggleButtonClass = (active: boolean) =>
    `px-3 py-1 text-sm rounded-md ${active ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-600'}`;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Table}
        title="Monthly Table View"
        subtitle={`Detailed monthly commission breakdown • ${COMMISSION_GST_BASIS}`}
      />

      <div>
      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6 overflow-visible">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Period Type Toggle */}
          <div>
            <span id="cb-period-type-label" className="block text-sm font-medium text-gray-400 mb-1">Period Type</span>
            <div role="group" aria-labelledby="cb-period-type-label" className="flex gap-x-1 bg-gray-700 border-gray-600 rounded-md p-1">
              {([
                ['calendar', 'Calendar'],
                ['financial', 'Financial'],
                ['allTime', 'All Time'],
              ] as [PeriodMode, string][]).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={periodMode === mode}
                  onClick={() => changePeriodMode(mode)}
                  className={toggleButtonClass(periodMode === mode)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Year Selector - hidden for All Time */}
          {periodMode !== 'allTime' && (
            <div className="relative z-50">
              <label htmlFor="cb-year" className="block text-sm font-medium text-gray-400 mb-1">
                {periodMode === 'financial' ? 'Financial Year' : 'Year'}
              </label>
              <select
                id="cb-year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {yearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Loan Book Selector: unified-mode companies with entity mappings only */}
          {hasEntityLoanBooks && (
            <div>
              <label htmlFor="cb-loan-book" className="block text-sm font-medium text-gray-400 mb-1">Loan Book</label>
              <select
                id="cb-loan-book"
                value={selectedLoanBook}
                onChange={(e) => setSelectedLoanBook(e.target.value)}
                className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Loan Books</option>
                {getDisplayLoanBooks(loanBooks as ResolvedLoanBook[]).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Gross / Payouts / Net: gross_only tenants with commission_deductions_view */}
          {showDeductions && (
            <div className="ml-auto">
              <span id="cb-view-label" className="block text-sm font-medium text-gray-400 mb-1">View</span>
              <div role="group" aria-labelledby="cb-view-label" className="flex gap-x-1 bg-gray-700 border-gray-600 rounded-md p-1">
                {([
                  ['gross', 'Gross commission'],
                  ['payouts', 'Payouts'],
                  ['net', 'Net retained'],
                ] as [ViewMode, string][]).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={viewMode === mode}
                    onClick={() => { setViewMode(mode); setExpandedMonths(new Set()); }}
                    className={toggleButtonClass(viewMode === mode)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {showDeductions && (
          <p className="mt-3 text-xs text-gray-400">
            {effectiveView === 'gross'
              ? 'Full commission company-wide, as billed by the aggregator (payouts not deducted).'
              : effectiveView === 'payouts'
                ? 'Amounts paid directly to brokers and referral partners. Click a month to see each payee.'
                : 'What the company retains after payouts (Gross − Payouts). Click a month to see per-payee detail.'}
          </p>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div aria-busy="true" aria-label="Loading commission breakdown">
          <SkeletonTable rows={12} columns={columns.length} />
        </div>
      )}

      {/* Error state: nothing from a previous filter is left on screen */}
      {!loading && error && (
        <div role="alert" className="p-4 bg-red-900/20 border border-red-500 rounded-lg">
          <p className="text-red-400 font-medium">Could not load the commission breakdown for {periodCaption}.</p>
          <p className="text-gray-400 text-sm mt-1">Please try again shortly. If this keeps happening, contact VALUTTEN support.</p>
        </div>
      )}

      {/* Sortable Table */}
      {!loading && !error && sortedData.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-10"
            style={{ background: 'radial-gradient(circle at 50% 0%, #3b82f640 0%, transparent 70%)' }}
          />

          <div className="overflow-x-auto relative z-10">
            <table className="w-full text-sm text-left text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-gray-900/50 sticky top-0">
                <tr>
                  {columns.map((c, i) => (
                    <th
                      key={c.key}
                      scope="col"
                      aria-sort={ariaSort(c.sortField)}
                      className={`px-4 py-3 whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}
                    >
                      <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'justify-end' : ''}`}>
                        <button
                          type="button"
                          onClick={() => handleSort(c.sortField)}
                          className="inline-flex items-center gap-1 uppercase hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        >
                          {c.label}
                          {sortIcon(c.sortField)}
                        </button>
                        {c.help && (
                          <span className="normal-case font-normal tracking-normal">
                            <HelpTooltip
                              content={{ definition: c.help }}
                              ariaLabel={`About ${c.label}`}
                              iconSize={12}
                              align={i >= columns.length - 3 ? 'right' : 'left'}
                            />
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => {
                  const canExpand = expandable(row);
                  const isOpen = canExpand && expandedMonths.has(row.yearMonth);
                  return (
                    <React.Fragment key={row.yearMonth}>
                      <tr
                        className={`border-b border-gray-700 hover:bg-gray-700/30 ${canExpand ? 'cursor-pointer' : ''}`}
                        onClick={canExpand ? () => toggleMonth(row.yearMonth) : undefined}
                      >
                        {columns.map((c, i) => (
                          <td
                            key={c.key}
                            className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : 'whitespace-nowrap'} ${c.className}`}
                          >
                            {i === 0 && canExpand ? (
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={(e) => { e.stopPropagation(); toggleMonth(row.yearMonth); }}
                                className="inline-flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                              >
                                <ChevronRight
                                  className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                  aria-hidden="true"
                                />
                                {c.cell(row)}
                              </button>
                            ) : (
                              c.cell(row)
                            )}
                          </td>
                        ))}
                      </tr>
                      {isOpen && renderPayeeRows(row)}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-900/70 font-semibold">
                <tr className="border-t-2 border-gray-600">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''} ${c.className}`}
                    >
                      {c.footerHelp ? (
                        <span className="inline-flex items-center gap-1 justify-end">
                          {c.footer}
                          <span className="font-normal">
                            <HelpTooltip
                              content={{ definition: c.footerHelp }}
                              ariaLabel={`Why ${c.label} has no total`}
                              iconSize={12}
                              placement="top"
                              align="right"
                            />
                          </span>
                        </span>
                      ) : (
                        c.footer
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Summary footer */}
          <div className="mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
            <p>
              {periodMode === 'allTime'
                ? `Showing all historical data (${monthCount} months with activity)`
                : `Showing ${monthCount} month${monthCount === 1 ? '' : 's'} for ${periodCaption}`
              }
              {' • '}
              Click column headers to sort
              {effectiveView !== 'gross' && ' • Click a month to expand per-payee detail'}
              {queryTimeMs !== null && (
                <span className="text-gray-500"> • Query: {queryTimeMs}ms</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* No data message */}
      {!loading && !error && sortedData.length === 0 && (
        <div className="mt-8 p-4 bg-gray-800 rounded-lg text-center">
          <p className="text-gray-400">
            No commission data {periodMode === 'allTime' ? 'yet' : `for ${periodCaption}`}. Upload commission files to see analytics.
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

export default MonthlyTableDashboardPage;
